import React, { useCallback } from "react"
import moment from "moment"
import { Loader2, Plus, Send, TrashIcon } from "lucide-react"

import CatchCard from "~/library/CatchCard"
import CatchTable from "~/library/CatchTable"

import { formatCurrency, titleize } from "~/utils/helpers"

import type Payment from "~/types/Payment"

interface Props {
  payments: Payment[]
  currencyIso: string
  totalAmountCents?: number | null
  paidAmountCents: number
  remainingAmountCents?: number | null
  fullyPaid: boolean
  sendingInvoice: string | null
  onCreateInvoice: () => void
  onCopyLink: (link?: string | null) => void
  onSendInvoice: (paymentId: string) => void
  onDeletePayment: (paymentId: string) => void
}

const normalizedPaymentType = (type?: string | null) =>
  (type || "").replace(/[^a-z0-9]/gi, "").toLowerCase()

const PaymentsSection: React.FC<Props> = ({
  payments,
  currencyIso,
  paidAmountCents,
  sendingInvoice,
  onCreateInvoice,
  onCopyLink,
  onSendInvoice,
  onDeletePayment,
}) => {
  const formatPaymentStatus = useCallback((status: string, isOverdue: boolean) => {
    if (status === "paid") {
      return <span className="badge badge-lg bg-success">Paid</span>
    }

    if (status === "pending") {
      return (
        <span className={`badge badge-lg ${isOverdue ? "bg-danger" : "bg-warning"}`}>
          {isOverdue ? "Overdue" : "Pending"}
        </span>
      )
    }

    return <span className="badge badge-lg bg-secondary">Draft</span>
  }, [])

  const canSendInvoice = useCallback((payment: Payment) => {
    return normalizedPaymentType(payment.type) === "invoice" && payment.status === "draft"
  }, [])

  const renderPaymentActions = useCallback(
    (payment: Payment) => {
      const invoiceKey = payment.prefixId || payment.id
      const isSending = sendingInvoice === invoiceKey
      const paymentType = normalizedPaymentType(payment.type)
      const isPaidInvoice =
        paymentType === "invoice" && (payment.status === "paid" || !!payment.paidOn)
      const isSentInvoice = paymentType === "invoice" && !!payment.sentOn
      const canDelete =
        !["savedcardcharge", "instantpayment"].includes(paymentType) &&
        !isPaidInvoice &&
        !isSentInvoice

      return (
        <div className="d-flex gap-2 align-items-center">
          {isSending ? (
            <Loader2 className="text-primary spinner" size={20} />
          ) : (
            canSendInvoice(payment) && (
              <Send
                className="text-primary cursor-pointer"
                size={18}
                onClick={() => {
                  onSendInvoice(invoiceKey)
                }}
              />
            )
          )}

          {canDelete && (
            <TrashIcon
              className={`text-danger ${isSending ? "opacity-50" : "cursor-pointer"}`}
              size={18}
              onClick={isSending ? undefined : () => onDeletePayment(payment.id)}
            />
          )}
        </div>
      )
    },
    [canSendInvoice, onDeletePayment, onSendInvoice, sendingInvoice]
  )

  return (
    <CatchCard>
      <div className="d-flex align-items-center mb-3">
        <h4 className="card-title mb-0">Payments</h4>

        <button className="btn btn-primary ms-auto" onClick={onCreateInvoice}>
          <Plus className="me-2" />
          Create Invoice
        </button>
      </div>

      <CatchTable
        data={payments}
        emptyMessage="No Payments"
        footerData={{
          rowData: [
            {
              className: "table-light fw-semibold",
              cellData: [
                { content: "Total Paid", colSpan: 2 },
                {
                  content: formatCurrency({
                    cents: paidAmountCents,
                    currency_iso: currencyIso,
                  }),
                  colSpan: 8,
                },
              ],
            },
          ],
        }}
        columns={[
          { key: "type", header: "Type", formatter: (type: string) => titleize(type) },
          {
            key: "createdBy",
            header: "Created By",
            formatter: (createdBy: Payment["createdBy"]) => createdBy?.name || createdBy?.email,
          },
          {
            key: "amount",
            header: "Total",
            formatter: (amount: Payment["amount"]) => formatCurrency(amount),
          },
          {
            key: "processingFeeCents",
            header: "Processing Fee",
            formatter: (feeCents: number, payment: Payment) =>
              formatCurrency({
                cents: feeCents || 0,
                currency_iso: payment.amount.currency_iso,
              }),
          },
          {
            key: "sentOn",
            header: "Sent On",
            formatter: (date: string | null) =>
              date ? moment(date).format("MM/D/YYYY HH:mm") : "—",
          },
          {
            key: "paidOn",
            header: "Paid On",
            formatter: (date: string | null) =>
              date ? moment(date).format("MM/D/YYYY HH:mm") : "—",
          },
          {
            key: "status",
            header: "Status",
            formatter: (status: Payment["status"], data: Payment) =>
              formatPaymentStatus(status, data.isOverdue),
          },
          {
            key: "receiptUrl" as keyof Payment,
            header: "Link",
            formatter: (receiptUrl: string | null, payment: Payment) => {
              const link = payment.hostedInvoiceUrl || receiptUrl
              const label = payment.hostedInvoiceUrl ? "Invoice" : "Receipt"

              return link ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => onCopyLink(link)}
                >
                  {label}
                </button>
              ) : (
                "—"
              )
            },
          },
          {
            key: "id",
            header: "Actions",
            formatter: (_id: string, data: Payment) => renderPaymentActions(data),
          },
        ]}
      />
    </CatchCard>
  )
}

export default PaymentsSection
